import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";
import { trayHolderApi } from "../../../../redux/apis/tray-api";
import { errorMessage, failureMessage } from "./tray-list-state";
import { holderFromRow, holderLabel } from "./tray-holder";

const TOOLTIP_GAP = 6;
// Room the tooltip needs below the icon before it opens above it instead.
const TOOLTIP_HEIGHT = 56;

/**
 * "i" icon for a tray a drop list or pick list holds. Hovering or focusing it
 * shows which document holds the tray: the list row's answer at once, then a
 * fresh GET /tray/holder lookup in its place — the list can be minutes old and
 * trays are reserved and released as drop lists and pick lists are worked. If
 * the lookup fails, the list's answer stays with a note saying so.
 *
 * Portalled to <body> with fixed positioning for the same reason as
 * TrayRowActions: the virtual list's rows would clip an in-row popover. Closes
 * on scroll or resize, where the fixed position would drift off the icon.
 */
const TrayHolderInfo = ({ tray }) => {
  const [position, setPosition] = useState(null);
  const [holder, setHolder] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  const iconRef = useRef(null);
  const requestIdRef = useRef(0);
  const tooltipId = useId();

  const close = () => {
    // Drops a lookup still in flight, so it can't land after the tooltip closed.
    requestIdRef.current += 1;
    setPosition(null);
  };

  useEffect(() => {
    if (!position) return undefined;
    const closeOnMove = () => {
      requestIdRef.current += 1;
      setPosition(null);
    };
    document.addEventListener("scroll", closeOnMove, true);
    window.addEventListener("resize", closeOnMove);
    return () => {
      document.removeEventListener("scroll", closeOnMove, true);
      window.removeEventListener("resize", closeOnMove);
    };
  }, [position]);

  const open = () => {
    if (position) return;
    const rect = iconRef.current.getBoundingClientRect();
    const openUp = window.innerHeight - rect.bottom < TOOLTIP_HEIGHT + TOOLTIP_GAP;
    setPosition({
      left: rect.left + rect.width / 2,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + TOOLTIP_GAP }
        : { top: rect.bottom + TOOLTIP_GAP }),
    });
    setHolder(holderFromRow(tray));
    setRefreshError(null);

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    trayHolderApi(tray.tray_id)
      .then((res) => {
        if (requestId !== requestIdRef.current) return;
        const failed = failureMessage(res);
        if (failed) {
          setRefreshError(failed);
          return;
        }
        setHolder(res?.data?.data);
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        setRefreshError(errorMessage(err, "Request failed"));
      });
  };

  return (
    <>
      <button
        ref={iconRef}
        type="button"
        aria-label="Which document holds this tray"
        aria-describedby={position ? tooltipId : undefined}
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={open}
        onBlur={close}
        className="flex shrink-0 items-center text-primary hover:opacity-70"
      >
        <Info size={15} />
      </button>
      {position &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            style={{
              position: "fixed",
              ...position,
              transform: "translateX(-50%)",
              zIndex: 999,
              boxShadow: "0px 1px 3px 0px var(--popover-shadow-color)",
            }}
            className="pointer-events-none border border-[var(--popover-border-color)] bg-[var(--popover-bg-color)] rounded px-[10px] py-[6px] text-[length:--popover-font-size] font-[var(--popover-font-weight)] text-[var(--popover-fg-color)] whitespace-nowrap"
          >
            <div>{holderLabel(holder)}</div>
            {refreshError && (
              <div className="mt-1 text-[11px] opacity-70">Couldn't refresh: {refreshError}</div>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

export default TrayHolderInfo;
